import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect, { components, createFilter } from 'react-select';

import './style.scss';

export function OptionWithTestId(props) {
  const option = (
    <components.Option
      {...props}
      className={`${props.className} ${props.data.optionClassName || ''} ${props.data.rendersAsHeader ? 'react-select__option header' : ''}`}
      innerProps={{
        'data-testid': `${props.selectProps.id}[${props.data.label}]`,
        'data-tooltip-id': props.data.tooltip ? 'tooltip' : undefined,
        'data-tooltip-content': props.data.tooltip,
        'data-tooltip-class-name': props.data.tooltip ? 'select-option-tooltip' : undefined,
        ...props.innerProps,
      }}
    />
  );
  return props.data.rendersAsHeader ? <div className="react-select__group no-bottom-border">{option}</div> : option;
};

export function ControlWithTestId(props) {
  const controlTestId = props.selectProps.controlTestId || `${props.selectProps.id}-control`;
  return (
    <components.Control
      {...props}
      innerProps={{
        'data-testid': controlTestId,
        ...props.innerProps,
      }}
    />
  );
}

export const styles = {
  menu: ({ width, ...css }) => ({ ...css, maxWidth: '50vw', minWidth: width }),
}

function Select(props) {
  const {
    data,
    label,
    id,
    defaultValue,
    value,
    className,
    searchByLabelOnly,
    isSearchable = false,
    menuPositionFixed = true,
    clearValueAfterSelection = false,
    inputId,
    ...otherProps
  } = props;

  const defaultFilter = {
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: 'any',
    stringify: option => `${option.data.label} ${option.data.value}`,
    trim: true,
  };

  const getLabel = option => {
    const labelNode = option?.labelNode;
    if (labelNode) {
      return <React.Fragment key={`option_${option.value}`}>{labelNode}</React.Fragment>;
    }
    return <React.Fragment key={`option_${option.value}`}>{option.label}</React.Fragment>;
  };
 
  const dataForValue =
  data?.length > 0
    ? data
      .filter(d => d.options)
      .map(d => d.options)
      .flat()
      .concat(data.filter(d => !d.options))
    : data;

  return (
    <>
      <label htmlFor={id} className="visuallyhidden">
        {label}
      </label>
      <ReactSelect
        id={id}
        key={id}
        inputId={inputId || `${id}-input`}
        className={`${className || ''} react-select-container`}
        classNamePrefix="react-select"
        defaultValue={dataForValue.find(o => o.value === defaultValue)}
        value={
          value?.value || !isNaN(value?.value)
            ? value
            : value || !isNaN(value)
            ? dataForValue.find(o => o.value === value)
            : clearValueAfterSelection
            ? ''
            : undefined
        }
        onChange={props.onChange}
        options={data}
        unstyled
        styles={styles}
        isSearchable={isSearchable}
        menuPosition={menuPositionFixed ? 'fixed' : 'absolute'}
        menuPortalTarget={menuPositionFixed ? document.body : undefined}
        isDisabled={props.disabled}
        isOptionDisabled={(option) => option.disabled}
        filterOption={
          searchByLabelOnly ? createFilter({ stringify: option => option.data.label }) : createFilter(defaultFilter)
        }
        getOptionLabel={getLabel}
        components={{ Option: OptionWithTestId, Control: ControlWithTestId }}
        {...otherProps}
      />
    </>
  );
}

export default Select;

Select.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  ]),
  data: PropTypes.array.isRequired,
  isSearchable: PropTypes.bool,
  menuPositionFixed: PropTypes.bool,
  clearValueAfterSelection: PropTypes.bool,
};
